import { Box, Divider, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * Building blocks of a settings screen that is meant to GROW.
 *
 * The shape is a list, not a form: sections group settings by what they affect, and inside a
 * section every setting is one row -- its name on the left, its control on the right. Adding a
 * setting is then appending one `<SettingRow>`, with no layout to rethink, which is the whole point
 * of the arrangement: the cluster area is expected to gain more switches.
 *
 * Rows are deliberately kept to a single line. Explaining a setting under its name makes the list
 * harder to scan the longer it gets, so the context that is genuinely needed goes in the
 * description of its section, once, instead of under each row.
 */

/**
 * A titled group of settings.
 *
 * @param {Object} props Props object
 * @param {string} props.heading Name of the group
 * @param {string} [props.description] One line saying what the group affects
 * @param {React.ReactNode} props.children The rows of the group
 * @returns {JSX.Element} The section
 */
export const SettingsSection = ({ heading, description = '', children }) => (
  <Stack gap={1}>
    <Box>
      <Typography
        variant='overline'
        sx={{ color: 'text.secondary', letterSpacing: '0.08em', lineHeight: 1.6 }}
      >
        {heading}
      </Typography>
      {description !== '' && (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      )}
    </Box>
    <Stack divider={<Divider flexItem />}>
      {children}
    </Stack>
  </Stack>
)

SettingsSection.propTypes = {
  heading: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node
}

/**
 * One setting: its name, and the control that changes it.
 *
 * @param {Object} props Props object
 * @param {string} props.label Name of the setting
 * @param {React.ReactNode} props.children The control
 * @param {string} [props.controlWidth] Width of the control column
 * @returns {JSX.Element} The row
 */
export const SettingRow = ({ label, children, controlWidth = '14rem' }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'stretch', sm: 'center' }}
    justifyContent='space-between'
    gap={2}
    sx={{ py: 1.5 }}
  >
    <Typography variant='body2' sx={{ fontWeight: 500 }}>{label}</Typography>
    <Box sx={{ width: { xs: '100%', sm: controlWidth }, flexShrink: 0 }}>
      {children}
    </Box>
  </Stack>
)

SettingRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  controlWidth: PropTypes.string
}
